/* About Us — premium brand story page.

Hero    : tagline + animated mini candle chart loop background (CSS-canvas)
Mission : large quote-style block
Story   : 2024 → 2026 timeline with milestones
Why     : 6-card pillar grid (FIU compliant, security, matching, liquidity, INR ramp, support)
Locations: New Delhi HQ with map
Investors / Press : logo walls (placeholders, easy to swap)
Careers : "Join the team" CTA
Final   : Sign Up + Start Trading CTAs
*/
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Shield, Zap, Layers, IndianRupee, Headphones, BadgeCheck,
  ArrowUpRight, MapPin, Briefcase, Sparkles, CheckCircle2,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Reveal from "@/components/Reveal";

const C = {
  navy: "#0B1426",
  blue: "#022dee",
  blueDeep: "#011BB7",
  lavender: "#EFEDFB",
  lavender2: "#E2DEFA",
  gold: "#F0B83A",
  green: "#16A34A",
  red: "#DC2626",
  grey: "#6B7280",
  ink: "#111827",
  cream: "#F8F7FB",
};

const HQ_ADDRESS =
  "GD-ITL Northex Towers, Unit 201, 203 & 204, 2nd Floor, Netaji Subhash Place, Pitampura, North West Delhi, Delhi 110034";

// =============================================================================
// Hero — BTC price line from Binance (sparkline + live ticker)
// =============================================================================
function CandleBackground() {
  const canvasRef = useRef(null);
  const closesRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    const loadSparkline = async () => {
      try {
        const { data } = await api.get("/api/spot/sparkline/BTC/USDT", {
          params: { interval: "1h", limit: 48 },
        });
        if (!cancelled && Array.isArray(data?.closes)) {
          closesRef.current = data.closes.filter(Number.isFinite);
        }
      } catch { /* keep previous */ }
    };
    const pollTicker = async () => {
      try {
        const { data } = await api.get("/api/spot/ticker/BTC/USDT");
        const last = Number(data?.last);
        const arr = closesRef.current;
        if (arr.length >= 2 && Number.isFinite(last)) {
          closesRef.current = [...arr.slice(-47), last];
        }
      } catch { /* ignore */ }
    };
    loadSparkline();
    pollTicker();
    const sparkIv = setInterval(loadSparkline, 90_000);
    const tickIv = setInterval(pollTicker, 12_000);
    return () => {
      cancelled = true;
      clearInterval(sparkIv);
      clearInterval(tickIv);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let raf;
    const dpr = window.devicePixelRatio || 1;

    const sized = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    sized();
    window.addEventListener("resize", sized);

    const draw = () => {
      const closes = closesRef.current;
      const W = canvas.width / dpr;
      const H = canvas.height / dpr;
      ctx.clearRect(0, 0, W, H);
      if (closes.length < 2) {
        raf = requestAnimationFrame(draw);
        return;
      }

      const min = Math.min(...closes);
      const max = Math.max(...closes);
      const span = Math.max(1e-6, max - min);
      const padX = 24;
      const padTop = H * 0.18;
      const padBottom = H * 0.14;
      const usableH = H - padTop - padBottom;
      const step = (W - padX * 2) / (closes.length - 1);
      const y = (p) => padTop + (1 - (p - min) / span) * usableH;
      const pts = closes.map((p, i) => ({ x: padX + i * step, y: y(p) }));
      const up = closes[closes.length - 1] >= closes[0];
      const lineColor = up ? "rgba(34,197,94,0.85)" : "rgba(239,68,68,0.85)";
      const fillTop = up ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.16)";
      const fillBottom = up ? "rgba(34,197,94,0.01)" : "rgba(239,68,68,0.01)";

      // Subtle horizontal grid
      ctx.strokeStyle = "rgba(2,44,237,0.06)";
      ctx.lineWidth = 1;
      for (let i = 0; i <= 4; i++) {
        const gy = padTop + (usableH / 4) * i;
        ctx.beginPath();
        ctx.moveTo(padX, gy);
        ctx.lineTo(W - padX, gy);
        ctx.stroke();
      }

      // Gradient fill under the line
      const grad = ctx.createLinearGradient(0, padTop, 0, H - padBottom);
      grad.addColorStop(0, fillTop);
      grad.addColorStop(1, fillBottom);
      ctx.beginPath();
      pts.forEach((pt, i) => {
        if (i === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      });
      ctx.lineTo(pts[pts.length - 1].x, H - padBottom);
      ctx.lineTo(pts[0].x, H - padBottom);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();

      // Main price line with soft shadow
      ctx.save();
      ctx.shadowColor = up ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)";
      ctx.shadowBlur = 8;
      ctx.beginPath();
      pts.forEach((pt, i) => {
        if (i === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      });
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 2.5;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.restore();

      // End-point glow (live price)
      const last = pts[pts.length - 1];
      const glow = ctx.createRadialGradient(last.x, last.y, 0, last.x, last.y, 14);
      glow.addColorStop(0, up ? "rgba(34,197,94,0.45)" : "rgba(239,68,68,0.45)");
      glow.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(last.x, last.y, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = up ? "#16A34A" : "#DC2626";
      ctx.beginPath();
      ctx.arc(last.x, last.y, 4.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.stroke();

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener("resize", sized);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-55 pointer-events-none"
      style={{
        maskImage: "linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)",
      }}
      aria-hidden
    />
  );
}

const HERO_STATS = [
  { value: "250+", label: "Markets & pairs" },
  { value: "FIU-IND", label: "Registered entity" },
  { value: "24/7", label: "Trading & support" },
  { value: "INR", label: "UPI & bank rails" },
];

const HERO_PRODUCTS = [
  { label: "Spot trading", href: "/trade/spot/BTC-USDT" },
  { label: "OTC desk", href: "/otc" },
];

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="about-hero"
      style={{ background: `linear-gradient(180deg, #FFFFFF 0%, ${C.cream} 100%)` }}>
      <Header />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(2,44,237,0.12) 0%, transparent 60%)" }} />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(240,184,58,0.16) 0%, transparent 60%)" }} />
      </div>
      <div className="relative">
        <div className="absolute inset-0 lg:w-[58%]">
          <CandleBackground />
        </div>
        <div className="cm-container relative pt-14 pb-14 lg:pt-20 lg:pb-20">
          <div className="grid lg:grid-cols-[1fr_380px] gap-10 lg:gap-14 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold tracking-wide uppercase"
                style={{ background: C.lavender, color: C.blue }}>
                <Sparkles size={12} /> About CoinMBX
              </div>
              <h1 className="mt-6 max-w-[720px]"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 800,
                  fontSize: "clamp(36px, 5vw, 64px)",
                  lineHeight: "1.06",
                  letterSpacing: "-0.02em",
                  color: C.navy,
                }}>
                <span style={{ color: C.blue }}>Built in India.</span>{" "}
                Crypto trading, reimagined.
              </h1>
              <p className="mt-5 max-w-[560px] text-[16px] lg:text-[17px] leading-[1.75]" style={{ color: C.grey }}>
                CoinMBX is a next-generation, FIU-IND compliant cryptocurrency exchange engineered
                for serious traders, institutional desks, and first-time buyers alike — all on one
                modern, deeply-liquid trading floor.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {HERO_PRODUCTS.map((p) => (
                  <Link
                    key={p.label}
                    to={p.href}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-semibold border transition-colors hover:border-cm-blue/30"
                    style={{ background: "#fff", color: C.navy, borderColor: C.lavender2 }}
                  >
                    {p.label}
                    <ArrowUpRight size={13} className="opacity-50" />
                  </Link>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/register" data-testid="about-hero-cta-signup"
                  className="inline-flex items-center justify-center h-[52px] px-9 rounded-[12px] font-bold text-[15px] shadow-[0_14px_30px_-12px_rgba(2,44,237,0.55)] hover:-translate-y-0.5 transition-all"
                  style={{ background: C.blue, color: "#fff" }}>
                  Open an Account
                </Link>
                <Link to="/trade/spot/BTC-USDT" data-testid="about-hero-cta-trade"
                  className="inline-flex items-center justify-center h-[52px] px-9 rounded-[12px] font-bold text-[15px] hover:-translate-y-0.5 transition-all"
                  style={{ background: C.lavender, color: C.blue }}>
                  Explore the Exchange
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              {HERO_STATS.map((s) => (
                <div
                  key={s.label}
                  className="p-4 lg:p-5 rounded-[16px] border transition-all hover:-translate-y-0.5"
                  style={{ background: "rgba(255,255,255,0.92)", borderColor: C.lavender2 }}
                >
                  <div className="text-[22px] lg:text-[26px] font-extrabold tracking-tight" style={{ color: C.navy }}>
                    {s.value}
                  </div>
                  <div className="mt-1 text-[11px] lg:text-[12px] font-medium" style={{ color: C.grey }}>
                    {s.label}
                  </div>
                </div>
              ))}
              <div
                className="col-span-2 p-4 lg:p-5 rounded-[16px] border"
                style={{ background: `linear-gradient(135deg, ${C.blue} 0%, ${C.blueDeep} 100%)`, borderColor: "transparent" }}
              >
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: C.gold }}>
                  <Shield size={13} /> Compliance first
                </div>
                <p className="mt-2 text-[13px] leading-[1.65] text-white/90">
                  Registered with FIU — Govt. of India. KYC, AML, and transaction monitoring built into every product.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Mission & Vision
// =============================================================================
function Mission() {
  return (
    <section className="relative py-16 lg:py-20" style={{ background: "#fff" }} data-testid="about-mission">
      <div className="cm-container">
        <Reveal variant="fade-up">
          <div className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: C.blue }}>
            Our Mission
          </div>
        </Reveal>
        <Reveal variant="fade-up" delay={120}>
          <h2 className="mt-6 max-w-[1100px]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(32px, 4.5vw, 60px)",
              lineHeight: "1.1",
              letterSpacing: "-0.02em",
              color: C.navy,
            }}>
            To make digital assets a <span style={{ color: C.gold }}>safe, simple, and trusted</span> part
            of every Indian portfolio.
          </h2>
        </Reveal>
        <div className="mt-12 grid lg:grid-cols-3 gap-5 lg:gap-6">
          <Reveal variant="fade-up" delay={200}>
            <div className="p-6 lg:p-7 rounded-[18px] h-full" style={{ background: C.lavender }}>
              <div className="text-[12px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: C.blue }}>
                Vision
              </div>
              <p className="text-[17px] leading-[1.65] font-medium" style={{ color: C.navy }}>
                A world where any Indian — from a Mumbai trader to a Tier-3 city saver — can access
                global markets with the same speed, security, and cost-efficiency as a Wall Street
                desk.
              </p>
            </div>
          </Reveal>
          <Reveal variant="fade-up" delay={260}>
            <div className="p-6 lg:p-7 rounded-[18px] border-2 h-full" style={{ borderColor: C.lavender2 }}>
              <div className="text-[12px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: C.gold }}>
                Promise
              </div>
              <p className="text-[17px] leading-[1.65] font-medium" style={{ color: C.navy }}>
                Compliance first. Customer assets segregated. Transparent fees. No hidden spreads.
                The exchange you would expect a regulated bank to build — only faster.
              </p>
            </div>
          </Reveal>
          <Reveal variant="fade-up" delay={320}>
            <div className="p-6 lg:p-7 rounded-[18px] h-full" style={{ background: C.navy, color: "#fff" }}>
              <div className="text-[12px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: C.gold }}>
                Today
              </div>
              <ul className="space-y-3 text-[14px] leading-[1.6] text-white/85">
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: C.green }} />
                  Spot and OTC on one account
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: C.green }} />
                  INR on-ramp via UPI &amp; bank transfer
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: C.green }} />
                  24×7 human support &amp; live markets
                </li>
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Platform — what CoinMBX delivers today
// =============================================================================
const PLATFORM = [
  {
    Icon: Zap,
    title: "Spot trading",
    text: "Buy and sell major crypto pairs with transparent fees, live charts, and INR-friendly funding — built for everyday traders and active desks alike.",
  },
  {
    Icon: Layers,
    title: "OTC desk",
    text: "Request locked quotes for large tickets. Our desk supports treasuries, family offices, and institutions with same-day INR settlement.",
  },
  {
    Icon: IndianRupee,
    title: "INR on & off-ramp",
    text: "Deposit and withdraw in rupees via UPI, IMPS, and bank transfer — with KYC, whitelisting, and compliance built into every step.",
  },
];

function Platform() {
  return (
    <section className="relative py-16 lg:py-20" style={{ background: C.cream }} data-testid="about-platform">
      <div className="cm-container">
        <Reveal variant="fade-up">
          <div className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: C.blue }}>
            The platform
          </div>
        </Reveal>
        <Reveal variant="fade-up" delay={100}>
          <h2
            className="mt-5 max-w-[760px]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(28px, 3.6vw, 48px)",
              lineHeight: "1.12",
              letterSpacing: "-0.02em",
              color: C.navy,
            }}
          >
            One exchange. Every way you trade crypto in India.
          </h2>
        </Reveal>
        <Reveal variant="fade-up" delay={160}>
          <p className="mt-4 max-w-[620px] text-[15px] leading-[1.75]" style={{ color: C.grey }}>
            CoinMBX brings spot markets and institutional OTC together — with the same
            account, wallet, and compliance layer across every product.
          </p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {PLATFORM.map((item, i) => {
            const Icon = item.Icon;
            return (
              <Reveal key={item.title} variant="fade-up" delay={120 + i * 70}>
                <div
                  className="p-6 lg:p-7 rounded-[18px] h-full border transition-all hover:-translate-y-0.5 flex gap-4"
                  style={{ background: "#fff", borderColor: C.lavender2 }}
                  data-testid={`platform-card-${i}`}
                >
                  <div
                    className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                    style={{ background: C.lavender }}
                  >
                    <Icon size={20} style={{ color: C.blue }} />
                  </div>
                  <div>
                    <h3 className="text-[17px] font-bold mb-1.5" style={{ color: C.navy }}>
                      {item.title}
                    </h3>
                    <p className="text-[14px] leading-[1.7]" style={{ color: C.grey }}>
                      {item.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Why CoinMBX — pillar grid
// =============================================================================
const PILLARS = [
  { Icon: BadgeCheck, title: "FIU-IND Compliant", text: "Registered with India's Financial Intelligence Unit. KYC, transaction monitoring and STR/CTR reporting baked into the platform." },
  { Icon: Shield, title: "Bank-grade Security", text: "Cold-storage majority custody, hardware-secured signing, 2FA-everywhere and full audit logs on every action." },
  { Icon: Zap, title: "Lightning Matching", text: "When you place an order, our engine pairs it with the best available counter-order instantly — first by price, then by time. That means faster fills and fair execution, even when markets move quickly." },
  { Icon: Layers, title: "Deep Liquidity", text: "USDT, INR and crypto-crypto pairs with always-on order books, OTC desk for blocks and tight spreads at every size." },
  { Icon: IndianRupee, title: "INR On / Off-Ramp", text: "UPI, IMPS and bank-transfer rails with same-day settlement — built for the way India already pays." },
  { Icon: Headphones, title: "24×7 Human Support", text: "A real human, in your timezone, every hour of the year — not a chatbot loop." },
];

function WhyCoinMBX() {
  return (
    <section className="relative py-16 lg:py-20" style={{ background: "#fff" }} data-testid="about-why">
      <div className="cm-container">
        <Reveal variant="fade-up">
          <div className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: C.blue }}>
            Why CoinMBX
          </div>
        </Reveal>
        <Reveal variant="fade-up" delay={100}>
          <h2 className="mt-6 max-w-[900px]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(32px, 4vw, 56px)",
              lineHeight: "1.1",
              letterSpacing: "-0.02em",
              color: C.navy,
            }}>
            The pillars we are built on.
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map(({ Icon, title, text }, i) => (
            <Reveal key={title} variant="fade-up" delay={150 + i * 80}>
              <div className="p-8 rounded-[20px] h-full transition-all hover:-translate-y-1"
                data-testid={`pillar-${i}`}
                style={{ background: C.cream, border: `1px solid ${C.lavender2}` }}>
                <div className="w-[52px] h-[52px] rounded-[14px] grid place-items-center mb-6"
                  style={{ background: C.blue }}>
                  <Icon size={24} color="#fff" />
                </div>
                <h3 className="text-[20px] font-bold mb-3" style={{ color: C.navy }}>{title}</h3>
                <p className="text-[14px] leading-[1.7]" style={{ color: C.grey }}>{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// Pin position on /assets/india-map-dark.png (full-country view, object-contain)
const HQ_MAP_PIN = { left: "34%", top: "27%" };

function MapPinMarker() {
  return (
    <svg width="34" height="46" viewBox="0 0 24 36" className="drop-shadow-[0_8px_16px_rgba(2,44,237,0.35)]" aria-hidden>
      <path
        d="M12 0C5.37 0 0 5.37 0 12c0 8.25 12 24 12 24s12-15.75 12-24C24 5.37 18.63 0 12 0z"
        fill={C.blue}
      />
      <circle cx="12" cy="12" r="5.5" fill="#fff" />
      <circle cx="12" cy="12" r="2.5" fill={C.gold} />
    </svg>
  );
}

function LocationMap() {
  const hq = HQ_MAP_PIN;

  return (
    <div
      className="relative rounded-[24px] overflow-hidden border bg-white shadow-[0_28px_56px_-32px_rgba(2,44,237,0.28)]"
      style={{ borderColor: C.lavender2 }}
      data-testid="about-location-map"
      aria-label="Map of India with headquarters marked in Pitampura, New Delhi"
    >
      <div
        className="relative aspect-[4/5] min-h-[340px] overflow-hidden p-4 sm:p-5"
        style={{ background: `linear-gradient(165deg, ${C.cream} 0%, ${C.lavender} 55%, #fff 100%)` }}
      >
        <div
          className="relative h-full w-full rounded-[22px] overflow-hidden border cm-about-map-frame"
          style={{
            background: "#fff",
            borderColor: C.lavender2,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 12px 36px -20px rgba(2,44,237,0.14)",
          }}
        >
          <img
            src="/assets/india-map-dark.png"
            alt="Map of India"
            className="absolute inset-0 w-full h-full object-contain p-2 pointer-events-none select-none"
            style={{ background: "#fff" }}
            draggable={false}
          />

          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                left: hq.left,
                top: hq.top,
                width: 80,
                height: 80,
                background: "radial-gradient(circle, rgba(2,44,237,0.35) 0%, transparent 70%)",
              }}
            />

            <div
              className="absolute flex flex-col items-center"
              style={{ left: hq.left, top: hq.top, transform: "translate(-50%, -100%)" }}
              data-testid="about-map-pin"
            >
              <div
                className="mb-2 rounded-[12px] px-3 py-2 border shadow-[0_10px_28px_-10px_rgba(2,44,237,0.65)]"
                style={{ background: "rgba(255,255,255,0.97)", borderColor: C.lavender2 }}
              >
                <div className="text-[10px] font-bold leading-none" style={{ color: C.blue }}>
                  Pitampura
                </div>
                <div className="text-[9px] font-semibold mt-0.5" style={{ color: C.grey }}>
                  New Delhi · HQ
                </div>
              </div>
              <MapPinMarker />
            </div>
          </div>
        </div>

        <div
          className="absolute top-7 left-7 sm:top-8 sm:left-8 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] border z-10"
          style={{ background: "rgba(255,255,255,0.92)", color: C.blue, borderColor: C.lavender2 }}
        >
          Built in India
        </div>
      </div>

      {/* HQ footer */}
      <div className="px-5 py-5 lg:px-6 lg:py-6 border-t bg-white" style={{ borderColor: C.lavender2 }}>
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-xl grid place-items-center shrink-0 shadow-[0_10px_20px_-12px_rgba(2,44,237,0.5)]"
            style={{ background: `linear-gradient(135deg, ${C.blue} 0%, ${C.blueDeep} 100%)` }}
          >
            <MapPin size={18} color="#fff" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.blue }}>
              Headquarters
            </div>
            <div className="text-[16px] font-bold leading-tight mt-0.5" style={{ color: C.navy }}>
              Pitampura, New Delhi
            </div>
            <div className="text-[12px] mt-1 leading-relaxed" style={{ color: C.grey }}>
              Netaji Subhash Place · Delhi 110034
            </div>
            <div className="text-[11px] mt-2 leading-relaxed text-cm-grey/80">
              GD-ITL Northex Towers, Pitampura
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Locations() {
  return (
    <section className="relative py-16 lg:py-20" style={{ background: "#fff" }} data-testid="about-locations">
      <div className="cm-container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal variant="fade-up">
            <div className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: C.blue }}>
              Where we work
            </div>
            <h2 className="mt-6"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: "clamp(32px, 4vw, 56px)",
                lineHeight: "1.1",
                letterSpacing: "-0.02em",
                color: C.navy,
              }}>
              Headquartered in New Delhi.<br />Built for India.
            </h2>
            <p className="mt-6 text-[16px] leading-[1.8] max-w-[520px]" style={{ color: C.grey }}>
              Our engineering, compliance and customer-experience teams operate out of the
              National Capital Region. We&rsquo;re a remote-friendly company supported by a
              human helpdesk available every hour of the year.
            </p>
            <div className="mt-10 space-y-5">
              {[
                {
                  city: "Pitampura, New Delhi",
                  role: "Headquarters",
                  note: HQ_ADDRESS,
                },
                {
                  city: "Helpdesk",
                  role: "24×7",
                  note: "helpdesk@coinmbx.com · +91 87961 01200",
                },
                {
                  city: "Legal Entity",
                  role: "Operator",
                  note: "Megabyte Digispace Technologies Private Limited",
                },
              ].map((o) => (
                <div key={o.city} className="flex items-start gap-4 p-5 rounded-[14px]"
                  style={{ background: C.cream, border: `1px solid ${C.lavender2}` }}
                  data-testid={`about-office-${o.city.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-[44px] h-[44px] rounded-full grid place-items-center shrink-0"
                    style={{ background: C.lavender }}>
                    <MapPin size={20} style={{ color: C.blue }} />
                  </div>
                  <div>
                    <div className="text-[16px] font-bold" style={{ color: C.navy }}>
                      {o.city} <span className="font-normal text-[13px] ml-2" style={{ color: C.gold }}>{o.role}</span>
                    </div>
                    <div className="text-[13px] mt-1" style={{ color: C.grey }}>{o.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal variant="fade-up" delay={200}>
            <LocationMap />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Career CTA
// =============================================================================
function Careers() {
  return (
    <section className="relative py-16 lg:py-20" style={{ background: "#fff" }} data-testid="about-careers">
      <div className="cm-container">
        <Reveal variant="fade-up">
          <div className="rounded-[28px] p-12 lg:p-20 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${C.navy} 0%, #1A2547 100%)`,
              color: "#fff",
            }}>
            {/* Gold orb */}
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${C.gold} 0%, transparent 60%)`, opacity: 0.18 }} />
            <div className="relative grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] mb-5"
                  style={{ color: C.gold }}>
                  <Briefcase size={14} /> Careers at CoinMBX
                </div>
                <h2 className="max-w-[640px]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 800,
                    fontSize: "clamp(28px, 3.6vw, 48px)",
                    lineHeight: "1.1",
                    letterSpacing: "-0.02em",
                    color: "#fff",
                  }}>
                  Help us build the most trusted crypto exchange in India.
                </h2>
                <p className="mt-5 max-w-[520px] text-[15px] leading-[1.8]"
                  style={{ color: "rgba(255,255,255,0.7)" }}>
                  We&rsquo;re hiring across engineering, compliance, design, and customer experience.
                  Remote-friendly, equity for early hires, and a chance to ship product to millions.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.linkedin.com/company/coinmbx/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="careers-cta-linkedin"
                  className="inline-flex items-center gap-2 h-[56px] px-8 rounded-[12px] font-bold text-[15px] hover:-translate-y-0.5 transition-all"
                  style={{ background: C.gold, color: C.navy }}>
                  See open roles
                  <ArrowUpRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// =============================================================================
// Final CTA
// =============================================================================
function FinalCTA() {
  return (
    <section className="py-16 lg:py-20" style={{ background: C.cream }} data-testid="about-final-cta">
      <div className="cm-container text-center">
        <Reveal variant="fade-up">
          <h2
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(36px, 5vw, 72px)",
              lineHeight: "1.05",
              letterSpacing: "-0.02em",
              color: C.navy,
            }}>
            Ready to join the next generation of Indian traders?
          </h2>
        </Reveal>
        <Reveal variant="fade-up" delay={150}>
          <p className="mt-6 max-w-[600px] mx-auto text-[16px] leading-[1.8]" style={{ color: C.grey }}>
            Sign up in under two minutes. KYC instantly. Trade securely.
          </p>
        </Reveal>
        <Reveal variant="fade-up" delay={250}>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/register" data-testid="about-final-cta-signup"
              className="inline-flex items-center justify-center h-[60px] px-12 rounded-[14px] font-bold text-[16px] shadow-[0_14px_30px_-12px_rgba(2,44,237,0.55)] hover:-translate-y-0.5 transition-all"
              style={{ background: C.blue, color: "#fff" }}>
              Create Account
            </Link>
            <Link to="/trade/spot/BTC-USDT" data-testid="about-final-cta-trade"
              className="inline-flex items-center justify-center h-[60px] px-12 rounded-[14px] font-bold text-[16px] hover:-translate-y-0.5 transition-all"
              style={{ background: "#fff", color: C.blue, border: `1px solid ${C.lavender2}` }}>
              Start Trading
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// =============================================================================
// Page
// =============================================================================
export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen" data-testid="about-page" style={{ background: "#fff" }}>
      <Hero />
      <Mission />
      <Platform />
      <WhyCoinMBX />
      <Locations />
      <Careers />
      <FinalCTA />
      <Footer />
    </div>
  );
}
